/**
 * MODULE: _shared/frontend/lib/frontend/api/brainMemoryApi.ts
 * PURPOSE: GET/DELETE /api/v1/assistant/memory/preferences — learned preference management.
 * DEPENDENCIES: axios, ./config
 * PUBLIC_API: fetchBrainMemoryPreferences, dismissBrainMemoryPreference, BrainMemoryPreference
 * SIDE_EFFECTS: HTTP to API gateway
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 58 / 150 lines
 * RECENT: 2026-05-26 [2.2.1253] — `shouldShowBrainLearningStatusBanner()` type predicate (Frontend Tests typecheck).
 * RECENT_PRIOR: 2026-05-26 [2.2.1246] — rep_contact_patterns on fetch for Dashboard/Home brain strip parity.
 * RECENT_PRIOR: 2026-05-26 [2.2.1245] — `shouldShowBrainLearningStatusBanner()` for idle onboarding surfaces; `next_step` on `BrainUserLearningStatus`.
 * RECENT_PRIOR: 2026-05-25 [2.2.1224] — promotionPipeline on fetch for Settings calibration strip.
 * RECENT_PRIOR: 2026-05-25 [2.2.1212] — promotionConfig on fetch.
 */

import axios from 'axios';
import { API_CONFIG, getAuthHeaders } from './config';
import { notifyBrainPreferencesChanged } from '../utils/brainPreferenceEvents';
import type { AssistantRepContactPatterns } from './assistantContextApi';

/** Shared React Query key for Settings + Home brain surfaces [2.2.1236]. */
export const BRAIN_MEMORY_PREFERENCES_QUERY_KEY = ['brain', 'memory-preferences'] as const;

export type BrainMemoryPreference = {
  id: string;
  memory_type?: string;
  memory_key?: string;
  memory_value?: string;
  confidence?: number;
  /** Interaction count when promoted (metadata or linked learning_candidate). */
  recurrence_count?: number;
  user_id?: string | null;
  source?: string;
  created_at?: string;
  updated_at?: string;
};

export type BrainMemoryPromotionConfig = {
  min_recurrence?: number;
  min_confidence?: number;
  scan_limit?: number;
};

export type BrainCoachingPatternStats = {
  sales_coaching_watched?: number;
  pipeline_coaching_watched?: number;
  sales_coaching_promoted?: number;
  pipeline_coaching_promoted?: number;
};

export type BrainMemoryPromotionPipeline = {
  pending_candidates?: number;
  eligible_candidates?: number;
  almost_ready_candidates?: number;
  active_memories?: number;
  learning_guidance?: string;
  /** Rep coaching self-learning counts from promotion pipeline [2.2.1316]. */
  coaching_patterns?: BrainCoachingPatternStats;
};

export type BrainContextHarness = {
  mode?: 'v3_char' | 'v4_token' | string;
  token_budget?: number | null;
  char_cap?: number;
};

export type BrainUserLearningStatus = {
  state?: 'catching_up' | 'almost_ready' | 'ready' | 'watching' | 'active' | 'idle' | string;
  headline?: string;
  detail?: string;
  catch_up_delayed?: boolean;
  /** Actionable hint for self-learning UX [2.2.1245]. */
  next_step?: string | null;
  /** When set, dashboard next_step opens a UI surface instead of auto-sending chat [2.2.1330]. */
  next_step_action?: 'open_sales_target_set' | string | null;
};

/** Show banner for active learning states, or idle when backend supplies onboarding next_step. */
export function shouldShowBrainLearningStatusBanner(
  status?: BrainUserLearningStatus | null,
): status is BrainUserLearningStatus {
  if (!status) return false;
  if ((status.state || 'idle') !== 'idle') return true;
  return Boolean((status.next_step || '').trim());
}

export type BrainMemoryPreferencesResponse = {
  tenant_id?: string;
  count?: number;
  items?: BrainMemoryPreference[];
  promotion_config?: BrainMemoryPromotionConfig;
  promotion_pipeline?: BrainMemoryPromotionPipeline;
  context_harness?: BrainContextHarness;
  learning_status?: BrainUserLearningStatus;
  rep_contact_patterns?: AssistantRepContactPatterns | null;
};

export type BrainMemoryPreferencesResult = {
  items: BrainMemoryPreference[];
  promotionConfig?: BrainMemoryPromotionConfig;
  promotionPipeline?: BrainMemoryPromotionPipeline;
  contextHarness?: BrainContextHarness;
  learningStatus?: BrainUserLearningStatus;
  repContactPatterns?: AssistantRepContactPatterns | null;
};

export async function fetchBrainMemoryPreferences(
  params: { scope?: 'tenant' | 'mine'; limit?: number } = {},
  signal?: AbortSignal,
): Promise<BrainMemoryPreferencesResult> {
  const { data } = await axios.get<BrainMemoryPreferencesResponse>(
    `${API_CONFIG.baseURL}/api/v1/assistant/memory/preferences`,
    {
      headers: getAuthHeaders(),
      timeout: API_CONFIG.timeout,
      signal,
      params: {
        scope: params.scope ?? 'mine',
        limit: params.limit ?? 50,
      },
    },
  );
  return {
    items: data?.items ?? [],
    promotionConfig: data?.promotion_config,
    promotionPipeline: data?.promotion_pipeline,
    contextHarness: data?.context_harness,
    learningStatus: data?.learning_status,
    repContactPatterns: data?.rep_contact_patterns,
  };
}

export async function dismissBrainMemoryPreference(memoryId: string): Promise<void> {
  await axios.delete(
    `${API_CONFIG.baseURL}/api/v1/assistant/memory/preferences/${encodeURIComponent(memoryId)}`,
    {
      headers: getAuthHeaders(),
      timeout: API_CONFIG.timeout,
    },
  );
  notifyBrainPreferencesChanged();
}
